/**
 * useAnalytics — Hook de tracking GA4 e-commerce centralisé.
 *
 * Tous les événements e-commerce GA4 passent ici.
 * La variable window.trackEvent est initialisée dans index.html.
 *
 * Utilisation dans les composants :
 *   const { trackAddToCart, trackPurchase, trackViewItem } = useAnalytics()
 */

import { liste } from '../utils/listes'
export function useAnalytics() {
  const push = (eventName, params) => {
    try {
      window.trackEvent?.(eventName, params)
    } catch (e) {
      // Ne jamais laisser le tracking planter l'app
    }
  }

  /**
   * Vue d'un produit (page produit)
   */
  const trackViewItem = (product, lang = 'fr') => push('view_item', {
    currency: 'GNF',
    value: product.price,
    items: [{
      item_id:       product.slug,
      item_name:     lang === 'fr' ? product.name_fr : (product.name_en || product.name_fr),
      item_category: product.category?.slug || '',
      price:         product.price,
      quantity:      1,
    }],
  })

  /**
   * Ajout au panier
   */
  const trackAddToCart = (product, quantity = 1, lang = 'fr') => push('add_to_cart', {
    currency: 'GNF',
    value:    product.price * quantity,
    items: [{
      item_id:       product.slug || product.id,
      item_name:     lang === 'fr' ? product.name_fr : (product.name_en || product.name_fr),
      item_category: product.category?.slug || '',
      price:         product.price,
      quantity,
    }],
  })

  /**
   * Suppression du panier
   */
  const trackRemoveFromCart = (product, quantity = 1) => push('remove_from_cart', {
    currency: 'GNF',
    value:    product.unit_price * quantity,
    items: [{
      item_id:  product.product_slug || product.slug,
      item_name: product.name?.fr || product.name_fr || '',
      price:    product.unit_price,
      quantity,
    }],
  })

  /**
   * Début du checkout
   */
  const trackBeginCheckout = (cartItems, total) => push('begin_checkout', {
    currency: 'GNF',
    value:    total,
    items:    cartItems.map(i => ({
      item_id:  i.product_slug,
      item_name: i.name?.fr || '',
      price:    i.unit_price,
      quantity: i.quantity,
    })),
  })

  /**
   * Achat finalisé (appelé sur la page de confirmation)
   */
  const trackPurchase = (order) => push('purchase', {
    transaction_id: order.order_number,
    currency:       'GNF',
    value:          order.total,
    shipping:       order.delivery_fee || 0,
    coupon:         order.coupon_code || '',
    items:          liste(order.items, 'articles pour la telemetrie').map(i => ({
      item_id:  i.product_slug,
      item_name: i.name?.fr || '',
      price:    i.unit_price,
      quantity: i.quantity,
    })),
  })

  /**
   * Ajout à la liste de souhaits
   */
  const trackAddToWishlist = (product, lang = 'fr') => push('add_to_wishlist', {
    currency: 'GNF',
    value:    product.price,
    items: [{
      item_id:  product.slug,
      item_name: lang === 'fr' ? product.name_fr : (product.name_en || product.name_fr),
      price:    product.price,
      quantity: 1,
    }],
  })

  /**
   * Recherche
   */
  const trackSearch = (term) => push('search', { search_term: term })

  /**
   * Login / Sign-up
   */
  const trackLogin  = (method = 'email') => push('login',  { method })
  const trackSignUp = (method = 'email') => push('sign_up', { method })

  return {
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackPurchase,
    trackAddToWishlist,
    trackSearch,
    trackLogin,
    trackSignUp,
  }
}
